import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Head, MainContent, ButtonGroup } from "./styled";
import { ns as baseNs } from "./config";
import { useAllSpecsQuery } from "@/hooks/queries/useAllSpecsQuery";
import QueryRequest from "_c/QueryRequest";
import isempty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import TextInput from "_c/form/TextInput";
import Empty from "_c/Empty";
import { Button } from "_c/button";
import { ServiceAPI } from "@/api/service";
import NetworkRequest from "_c/NetworkRequest";
import capitalize from "lodash/capitalize";
import { analyticsAction } from "@/utils/analytics";
import { useMasterId } from "@/hooks/useMasterId";
import { useLocale } from "@/hooks/useLocale";
import { useMasterServiceEditor } from "@/hooks/useMasterServiceEditor";
const ns = baseNs + "step_service.";

const StepService = ({ t, goNext }) => {
  const masterId = useMasterId();
  const [spec, setSpec] = useState(null);
  const onServiceCreate = () => {
    analyticsAction({
      category: "wizard",
      action: "service_added",
    });
    goNext();
  };
  const openServiceEditor_ = useMasterServiceEditor();
  const openServiceEditor = (args) => {
    openServiceEditor_({
      masterId,
      onCreate: onServiceCreate,
      defaultSpec: spec,
      ...args,
    });
  };
  return (
    <>
      <Head>{t(ns + (spec ? "title_service" : "title_spec"))}</Head>
      {!spec ? (
        <SpecSelector onSkip={goNext} onSelect={setSpec} t={t} />
      ) : (
        <TitleSelector
          onSkip={goNext}
          goBack={() => setSpec(null)}
          t={t}
          spec={spec}
          onSelect={(title) => {
            openServiceEditor({
              defaultTitle: title,
            });
          }}
        />
      )}
    </>
  );
};

const SpecSelector = (props) => {
  const specsQuery = useAllSpecsQuery();
  const queryData = specsQuery.data;
  const [search, setSearch] = useState("");
  useEffect(() => {
    analyticsAction({
      category: "wizard",
      action: "spec_screen",
    });
  }, []);
  const specs = useMemo(() => {
    if (isempty(queryData)) return [];
    return orderBy(
      Object.entries(queryData).map(([id, { title }]) => ({
        value: id,
        label: title,
      })),
      "label",
    );
  }, [queryData]);
  const filteredSpecs = useMemo(() => {
    if (!search) return specs || [];
    if (isempty(specs)) return [];
    const lower = search.toLowerCase();
    return specs.filter(({ label }) => label.toLowerCase().includes(lower));
  }, [specs, search]);
  return (
    <QueryRequest query={specsQuery}>
      <>
        <TextInput
          css={`
            margin-bottom: 15px;
            flex-shrink: 0;
          `}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={props.t("common:search")}
        />
        {isempty(filteredSpecs) ? (
          <Empty message={props.t("common:empty")} />
        ) : (
          <MainContent as="ul">
            {filteredSpecs.map((spec) => (
              <li
                onClick={() => {
                  analyticsAction({
                    category: "wizard",
                    action: "spec_chosen",
                  });
                  props.onSelect(spec.value);
                }}
                role="button"
                css={`
                  background-color: var(--bg-elevate);
                  padding: 10px;
                  cursor: pointer;
                  margin-bottom: 7px;
                  border-radius: 9px;
                `}
                key={spec.value}
              >
                {spec.label}
              </li>
            ))}
          </MainContent>
        )}
      </>
      <ButtonGroup>
        <Button
          small
          negative
          onClick={() => {
            analyticsAction({
              category: "master_setup_wizard",
              action: "nospec_button",
            });
            props.onSkip();
          }}
        >
          {props.t(ns + "no_spec")}
        </Button>
      </ButtonGroup>
    </QueryRequest>
  );
};

const defaultValue = [];
const selector = (data) =>
  data.map(({ title }) => ({
    value: title,
    label: title,
  }));
const TitleSelector = (props) => {
  const locale = useLocale();
  const [search, setSearch] = useState("");
  const loader = useCallback(() => {
    return ServiceAPI.getTitleCompletion({
      title: search,
      locale,
      spec: props.spec,
    });
  }, [props.spec, locale, search]);
  return (
    <>
      <TextInput
        value={search}
        autoFocus
        onChange={(e) => setSearch(e.target.value)}
        placeholder={props.t("common:search")}
        css={`
          margin-bottom: 15px;
          flex-shrink: 0;
        `}
      />
      <MainContent>
        <NetworkRequest
          isEnabled={search.length > 2}
          defaultValue={defaultValue}
          defaultLoadingState="idle"
          loader={loader}
          select={selector}
        >
          {({ data }) => (
            <>
              {isempty(data) ? (
                <>
                  {search.length > 2 && (
                    <Button
                      onClick={() => props.onSelect(capitalize(search))}
                      greenish
                      small
                    >
                      {props.t("common:create_some", { name: search })}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {data.map((title) => (
                    <div
                      onClick={() => props.onSelect(title.value)}
                      role="button"
                      css={`
                        background-color: var(--bg-elevate);
                        padding: 10px;
                        cursor: pointer;
                        margin-bottom: 7px;
                        border-radius: 9px;
                      `}
                      key={title.value}
                    >
                      {title.label}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </NetworkRequest>
      </MainContent>
      <ButtonGroup>
        <Button onClick={props.goBack} small>
          {props.t("common:prev")}
        </Button>
        <Button small negative onClick={props.onSkip}>
          {props.t("common:skip")}
        </Button>
      </ButtonGroup>
    </>
  );
};

export default StepService;
